import { Shield, Users, Mail, Key, Eye, FileCheck } from "lucide-react";

export const PhishingPrevention = () => {
  const preventionMeasures = [
    {
      icon: Users,
      title: "Security Awareness Training",
      description: "Regular training and simulated phishing tests. Make reporting easy and non-punitive.",
      priority: "Critical",
      color: "text-destructive",
    },
    {
      icon: Mail,
      title: "Email Authentication (SPF/DKIM/DMARC)",
      description: "Properly configured email authentication prevents spoofing of your domain.",
      priority: "Critical",
      color: "text-destructive",
    },
    {
      icon: Key,
      title: "Multi-Factor Authentication (MFA)",
      description: "Even if credentials are stolen, MFA prevents account takeover. Use hardware keys for high-value accounts.",
      priority: "Critical",
      color: "text-destructive",
    },
    {
      icon: Eye,
      title: "Email Filtering & Scanning",
      description: "Use email security gateways that scan links and attachments in real-time.",
      priority: "High",
      color: "text-chart-1",
    },
    {
      icon: Shield,
      title: "DNS-based Protection",
      description: "Block known malicious domains at the DNS level to prevent connections even if links are clicked.",
      priority: "High",
      color: "text-chart-1",
    },
    {
      icon: FileCheck,
      title: "Incident Response Plan",
      description: "Have a clear process for when phishing succeeds: credential reset, forensics, and communication.",
      priority: "High",
      color: "text-chart-1",
    },
  ];

  return (
    <section className="py-8 sm:py-12">
      <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mb-6">
        Prevention & Protection Strategies
      </h2>
      <p className="text-muted-foreground mb-8 max-w-3xl">
        A layered defense approach is essential. No single measure is sufficient.
      </p>

      <div className="space-y-4">
        {preventionMeasures.map((measure, index) => (
          <div
            key={index}
            className="bg-card rounded-xl border border-border p-6 flex items-start gap-4 hover:border-primary/30 transition-colors"
          >
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <measure.icon className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="font-semibold text-foreground">{measure.title}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  measure.priority === "Critical" 
                    ? "bg-destructive/10 text-destructive" 
                    : "bg-chart-1/10 text-chart-1"
                }`}>
                  {measure.priority}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">{measure.description}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-2xl border border-primary/20 p-6 sm:p-8">
        <h3 className="text-lg font-semibold text-foreground mb-3">If You Suspect a Phishing Attempt</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            "Don't click any links or download attachments",
            "Don't reply to the email",
            "Report it to your IT/security team immediately",
            "If you clicked a link, change your password now",
            "Forward the email to your security team (as attachment)",
            "Enable MFA on all accounts if not already done",
          ].map((item, index) => (
            <div key={index} className="flex items-start gap-2 text-sm text-foreground">
              <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center shrink-0 mt-0.5">
                {index + 1}
              </span>
              {item}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
